import { motion } from "framer-motion";
import { Check, Zap } from "lucide-react";
import { PACKAGES } from "@/lib/packages";

function currentWindow() {
  try {
    const parts = new Intl.DateTimeFormat("it-IT", {
      timeZone: "Europe/Rome", weekday: "short", hour: "numeric", hour12: false,
    }).formatToParts(new Date());
    const wd = parts.find((p) => p.type === "weekday").value.toLowerCase();
    const hour = parseInt(parts.find((p) => p.type === "hour").value, 10) % 24;
    const isWeekday = ["lun", "mar", "mer", "gio", "ven"].some((d) => wd.startsWith(d));
    return {
      baseOpen: isWeekday && hour >= 9 && hour < 18,
      premiumOpen: isWeekday && hour >= 9 && hour < 19,
      vipOpen: hour >= 7 && hour < 24,
    };
  } catch {
    return { baseOpen: true, premiumOpen: true, vipOpen: true };
  }
}

const UpsellBanner = ({ onSelect }) => {
  const { baseOpen, premiumOpen, vipOpen } = currentWindow();
  if (!vipOpen || (baseOpen && premiumOpen)) return null;

  const suggestVip = !premiumOpen;
  const pkg = PACKAGES.find((p) => p.id === (suggestVip ? "business" : "premium"));

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      data-testid="upsell-banner"
      className="mb-10 flex flex-wrap items-center justify-between gap-4 rounded-3xl border-2 border-gold/50 bg-gold/10 px-7 py-5"
    >
      <div className="flex items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gold">
          <Zap size={20} className="text-mossdeep" />
        </div>
        <p className="text-sm sm:text-base text-ink leading-relaxed max-w-xl">
          <span className="font-bold">In questo momento {suggestVip ? "Base e Premium non sono attivi" : "il Voucher Base non è attivo"}.</span>{" "}
          {suggestVip
            ? "Il Voucher VIP invece è utilizzabile adesso, con chiamata entro 2 ore dal contatto WhatsApp."
            : "Il Voucher Premium invece è attivo fino alle 19.00, con chiamata entro 24h."}
        </p>
      </div>
      <button
        onClick={() => onSelect(pkg)}
        data-testid="upsell-banner-cta"
        className="rounded-full bg-mossdeep px-6 py-3 text-sm font-semibold text-gold transition-all duration-200 hover:bg-moss hover:-translate-y-0.5"
      >
        Attiva il {pkg.tier} adesso
      </button>
    </motion.div>
  );
};

export const Pricing = ({ onSelect }) => (
  <section id="pacchetti" className="max-w-6xl mx-auto px-6 py-24">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className="text-center max-w-2xl mx-auto"
    >
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta font-bold mb-4">
        Pacchetti Voucher
      </p>
      <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug text-moss">
        Paghi la competenza,{" "}
        <span className="italic text-mosslight">non il furgone della ditta.</span>
      </h2>
      <p className="mt-4 text-base text-sub">
        Ogni voucher include chiamate specialistiche col Dott. Vincenti e non scade per 12 mesi.
      </p>
    </motion.div>

    <div className="mt-14">
      <UpsellBanner onSelect={onSelect} />
    </div>
    <div className="grid gap-6 lg:grid-cols-3 items-stretch">      {PACKAGES.map((pkg, i) => {
        const v = pkg.vip ? "vip" : pkg.popular ? "popular" : "base";
        const styles = {
          card: {
            base: "relative rounded-[1.75rem] bg-white border border-line p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(27,59,43,0.12)] flex flex-col",
            popular: "relative rounded-[1.75rem] bg-moss text-alabaster p-8 shadow-[0_30px_60px_rgba(15,23,20,0.35)] lg:-translate-y-4 flex flex-col",
            vip: "relative rounded-[1.75rem] bg-gold text-mossdeep p-8 border border-[#B98E41] shadow-[0_30px_60px_rgba(212,163,89,0.35)] lg:-translate-y-2 flex flex-col",
          },
          badge: {
            base: "self-start rounded-full bg-sage text-sagetext font-mono text-[11px] uppercase tracking-[0.2em] px-3.5 py-1.5 font-semibold",
            popular: "self-start rounded-full bg-gold/20 text-gold font-mono text-[11px] uppercase tracking-[0.2em] px-3.5 py-1.5 font-semibold",
            vip: "self-start rounded-full bg-mossdeep text-gold font-mono text-[11px] uppercase tracking-[0.2em] px-3.5 py-1.5 font-semibold",
          },
          title: { base: "text-moss", popular: "text-alabaster", vip: "text-mossdeep" },
          desc: { base: "text-sub", popular: "text-alabaster/70", vip: "text-mossdeep/75" },
          price: { base: "text-ink", popular: "text-gold", vip: "text-mossdeep" },
          priceNote: { base: "text-sub", popular: "text-alabaster/60", vip: "text-mossdeep/65" },
          mono: { base: "text-terracotta", popular: "text-gold/80", vip: "text-mossdeep/80" },
          check: { base: "text-mosslight", popular: "text-gold", vip: "text-mossdeep" },
          li: { base: "text-sub", popular: "text-alabaster/85", vip: "text-mossdeep/90" },
          button: {
            base: "mt-8 w-full rounded-full border-2 border-moss py-4 text-base font-semibold text-moss transition-all duration-200 hover:bg-moss hover:text-alabaster",
            popular: "mt-8 w-full rounded-full bg-terracotta py-4 text-base font-semibold text-alabaster transition-all duration-200 hover:bg-terrahover hover:-translate-y-0.5",
            vip: "mt-8 w-full rounded-full bg-mossdeep py-4 text-base font-semibold text-gold transition-all duration-200 hover:bg-moss hover:-translate-y-0.5",
          },
        };
        return (
        <motion.div
          key={pkg.id}
          data-testid={pkg.testid}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: i * 0.12 }}
          className={styles.card[v]}
        >
          <span className={styles.badge[v]}>
            {pkg.badge}
          </span>
          <h3 className={`mt-5 font-serif text-2xl font-semibold ${styles.title[v]}`}>
            {pkg.name}
          </h3>
          <p className={`mt-2 text-sm leading-relaxed ${styles.desc[v]}`}>
            {pkg.desc}
          </p>
          <div className="mt-6 flex items-end gap-2">
            <span className={`font-serif text-5xl font-bold tracking-tight ${styles.price[v]}`}>
              {pkg.priceNet}
            </span>
            <span className={`pb-1.5 text-xs ${styles.priceNote[v]}`}>
              + IVA ({pkg.priceGross} ivato)
            </span>
          </div>
          <p className={`mt-1 font-mono text-xs uppercase tracking-widest ${styles.mono[v]}`}>
            {pkg.tier}
          </p>
          <ul className="mt-6 space-y-3 flex-1">
            {pkg.includes.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm">
                <Check size={16} className={`mt-0.5 shrink-0 ${styles.check[v]}`} />
                <span className={styles.li[v]}>{f}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={() => onSelect(pkg)}
            data-testid={`buy-${pkg.id}-button`}
            className={styles.button[v]}
          >
            Acquista il Voucher
          </button>
        </motion.div>
        );
      })}
    </div>
    <p className="mt-8 text-center text-xs text-sub">
      Pagamento sicuro con Stripe (carta) · Fattura fiscale rilasciata a privati e partite IVA
    </p>
  </section>
);
